import { useContext, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { USER_ROLES } from '../constants';
import { useFetchOrganizations, useFetchFacilities, useFetchUnits } from './useDataFetching';
import { FacilityUnitContext } from '../context';
import { serviceLogger } from '../utils/serviceLogger';

const useDeviceSelector = () => {
  const {
    selectedFacility,
    setSelectedFacility,
    selectedOrganization,
    setSelectedOrganization,
    selectedUnit,
    setSelectedUnit,
  } = useContext(FacilityUnitContext);

  const userData = useSelector((state) => state?.auth?.user);
  const userType = userData?.role;
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [units, setUnits] = useState([]);
  const [facilityError, setFacilityError] = useState('');
  const [unitError, setUnitError] = useState('');
  const [loading, setLoading] = useState({
    organizations: false,
    facilities: false,
    units: false,
    search: false,
  });

  useFetchOrganizations({ userData, userType, setLoading, setOrganizations });
  useFetchFacilities({
    userData,
    userType,
    selectedOrganization,
    setSelectedOrganization,
    setFacilities,
    setFacilityError,
    setLoading,
  });
  useFetchUnits({ selectedFacility, setUnits, setUnitError, setLoading });

  const { organizationOptions, organizationMap } = useMemo(() => {
    return organizations.reduce(
      (acc, organization) => {
        if (organization?.id) {
          acc.organizationOptions.push(organization.name);
          acc.organizationMap[organization.id] = organization.name;
        }
        return acc;
      },
      { organizationOptions: [], organizationMap: {} },
    );
  }, [organizations]);

  const { facilityOptions, facilityMap } = useMemo(() => {
    return facilities.reduce(
      (acc, facility) => {
        if (facility?.id) {
          acc.facilityOptions.push(facility.name);
          acc.facilityMap[facility.id] = facility.name;
        }
        return acc;
      },
      { facilityOptions: [], facilityMap: {} },
    );
  }, [facilities]);

  const unitOptions = useMemo(() => units.map((unit) => unit.data_unit_name), [units]);

  const selectedFacilityName = facilityMap[selectedFacility] || '';
  const selectedOrganizationName = organizationMap[selectedOrganization] || '';

  const handleSearch = async () => {
    setLoading((prev) => ({ ...prev, search: true }));
    try {
      navigate(`/summary`);
    } catch (error) {
      serviceLogger.error('Error during search:', error);
    } finally {
      setLoading((prev) => ({ ...prev, search: false }));
    }
  };

  const selectionHandlers = {
    organization: (name) => {
      const organizationSelected = organizations.find(
        (organizationOption) => organizationOption.name === name,
      );
      if (organizationSelected?.id !== selectedOrganization) {
        setSelectedOrganization(organizationSelected?.id || null);
        setFacilities([]);
        setSelectedFacility(null);
        setUnits([]);
        setSelectedUnit(null);
      }
    },
    facility: (name) => {
      const facilitySelected = facilities.find((facilityOption) => facilityOption.name === name);
      if (facilitySelected?.id !== selectedFacility) {
        setSelectedFacility(facilitySelected?.id || null);
        setUnits([]);
        setSelectedUnit(null);
      }
    },
    unit: (name) => {
      const unitSelected = units.find((unitOption) => unitOption.data_unit_name === name);
      setSelectedUnit(unitSelected || null);
    },
  };

  const handleSelect = (type, name) => {
    if (!name) return;
    const handler = selectionHandlers[type];
    if (handler) handler(name);
    else serviceLogger.warn(`Unhandled selection type: ${type}`);
  };

  const handleClearFilters = () => {
    if (userType === USER_ROLES.ADMIN) {
      setSelectedOrganization(null);
      setFacilities([]);
    }
    setSelectedFacility(null);
    setSelectedUnit(null);
    setUnits([]);
    setFacilityError('');
    setUnitError('');
  };

  return {
    selectedFacility,
    selectedOrganization,
    selectedFacilityName,
    selectedUnit,
    userType,
    handleClearFilters,
    facilities,
    organizationOptions,
    facilityOptions,
    unitOptions,
    selectedOrganizationName,
    unitError,
    facilityError,
    handleSelect,
    handleSearch,
    loading,
  };
};

export default useDeviceSelector;
